<template>
  <div
    id="container"
    :class="[padColor, { 'app-shrunk': isIOSDrawerOpen && isMobile }]"
  >
    <div
      class="w-full h-[100dvh]"
      :style="{paddingTop: insets.topPadding, paddingBottom: insets.bottomPadding}">
      <router-view />
    </div>

    <UpdateModal
      :show="updateState.showModal.value as boolean"
      :stage="updateState.updateStage.value as UpdateStage"
    />
  </div>

  <IOSDrawer />
  <AuthErrorDialog />

  <ForceUpdateScreen
    :show="storeUpdateState.updateRequired.value"
    :version="storeUpdateState.availableVersion.value"
    :loading="storeUpdateState.isOpeningStore.value"
    @update="StoreUpdateService.openStore()"
  />
</template>

<script setup lang="ts">
import posthog from 'posthog-js';
import { Capacitor } from '@capacitor/core';
import { computed, onMounted } from 'vue';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { useRouter } from 'vue-router';
import { UpdateService, UpdateStage, updateState } from '@app/update-service';
import { StoreUpdateService, storeUpdateState } from '@app/store-update-service';
import { IOSDrawer, useDrawer, useSharedStore } from '@torim-app/shared-components';
import { useResponsive } from '@app/composables/responsive';
import { AdminRouteNames } from '@app/router/route-names';
import AuthErrorDialog from '@app/components/auth/AuthErrorDialog.vue';

const sharedStore = useSharedStore();
const router = useRouter();
const { isOpen: isIOSDrawerOpen } = useDrawer();
const { isMobile } = useResponsive();

const isDrawerOpen = computed(() => sharedStore.isDrawerOpen);

// On native the app is edge-to-edge and paints the safe-area strips (the areas
// behind the device status bar / gesture bar) itself. The More page is full-bleed
// dark, so match its gradient there instead of leaving the strips white.
const isDarkPage = computed(() => router.currentRoute.value.name === AdminRouteNames.More);

const padColor = computed<string>(() => {
  if ((sharedStore.topPadding === 0 && sharedStore.bottomPadding === 0) || isDrawerOpen.value)
    return '';
  return isDarkPage.value ? 'bg-gradient-to-b from-[#1b1136] to-[#24104A]' : 'bg-white';
});

const insets = computed<{ topPadding: string; bottomPadding: string; }>(() => {
  return sharedStore.getInset;
});

onMounted(async () => {
  if (Capacitor.isNativePlatform()) {
    // Block the app while a newer native version is live in the App Store / Google Play
    StoreUpdateService.init();

    // Check for live updates (Capawesome) when app starts
    await UpdateService.checkAndUpdate();

    SafeArea.getSafeAreaInsets()
      .then(({ insets }) => {
        sharedStore.setInsets(insets.top, insets.bottom);
        document.documentElement.style.setProperty('--top-safe-area', insets.top + 'px');
        document.documentElement.style.setProperty('--bottom-safe-area', insets.bottom + 'px');
      }).catch(e => {
      console.error(`Failed to init safe area: ${e}`);
    });
  }

  try {
    if (import.meta.env.VITE_POSTHOG_PROJECT_TOKEN)
      posthog.init(import.meta.env.VITE_POSTHOG_PROJECT_TOKEN, {
        api_host: 'https://us.i.posthog.com',
        defaults: '2026-01-30',
        capture_exceptions: true
      });
  } catch (e) {
    console.error('Failed to load posthog:', e);
  }
});
</script>

<style lang="scss">
@use '../assets/css/vars' as *;

.p-drawer {
  padding-top: v-bind('insets.topPadding');
  padding-bottom: v-bind('insets.bottomPadding');
}

body {
  flex-grow: 1;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  width: 100%;
  overflow: hidden;
  max-height: 100%;
  background: $lead-color-darker;

  &.scanner-active {
    background: transparent;
    --background: transparent;
    --ion-background-color: transparent;

    #rootapp {
      opacity: 0;
    }
  }

  .safe-area-wrapper {
    padding-top: v-bind('insets.topPadding');
    padding-bottom: v-bind('insets.bottomPadding');
  }

  #app {
    font-family: 'Rubik', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    margin: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;

    #container {
      max-height: 100%;
      min-height: 100dvh;
      height: 100dvw;
      overflow: hidden;
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;

      // --- iOS Shrink Animation Styles ---
      transition: transform 0.4s cubic-bezier(0.32, 0.72, 0, 1),
      border-radius 0.4s ease,
      opacity 0.4s ease;
      transform-origin: center top;

      &.app-shrunk {
        // Moves down slightly + scales + rounds corners
        transform: scale(0.93) translateY(calc(env(safe-area-inset-top) + 22px));
        border-radius: 16px;
        opacity: 0.8;
        pointer-events: none; // Disables interaction with background app
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1); // Optional: subtle border highlight
      }

      // -----------------------------------

      .loading {
        display: flex;
        width: 100%;
        height: 100%;
        flex-grow: 1;
        justify-content: center;
        align-items: center;
      }
    }
  }
}
</style>
